import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BackdropOverlay, {
    DIALOG_OVERLAY_TOP_OFFSET,
} from "../common/BackdropOverlay.jsx";
import { colors } from "../../theme/colors";
import { DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";
import {
    SCROLLBAR_THUMB_COLOR,
    SCROLLBAR_THUMB_HOVER_COLOR,
    SCROLLBAR_TRACK_COLOR,
} from "../../universal/scrollbarStyles";

const MotionBox = motion(Box);

const DIALOG_Z_INDEX = 1500;
const STEAM_SETTINGS_URL = "https://steamcommunity.com/my/edit/settings";

const DEFAULT_BLOCKED_MESSAGE =
    "Your Steam profile and CS2 playtime must be public before you can join matchmaking.";

const CHECKLIST_INTRO =
    "To fix this, open Steam privacy settings and make these changes:";

const CHECKLIST_STEPS = [
    {
        number: "1",
        title: "Set your Steam profile to Public.",
    },
    {
        number: "2",
        title: "Set Game details to Public.",
    },
    {
        number: "3",
        prefix: "Make sure",
        label: "Always keep my total playtime private",
        suffix: "is unchecked.",
    },
];

const CHECKLIST_FOLLOW_UP =
    "After saving in Steam, come back and press Play again. We will re-check your CS2 hours before adding you to the queue.";

const SECTION_TITLE_SX = {
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: colors.text.secondary,
    mb: "12px",
};

const SCROLLABLE_BODY_SX = {
    overflowY: "auto",
    overflowX: "hidden",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "thin",
    scrollbarColor: `${SCROLLBAR_THUMB_COLOR} ${SCROLLBAR_TRACK_COLOR}`,
    maxHeight: { xs: "min(52vh, 420px)", sm: "min(58vh, 480px)" },
    "&::-webkit-scrollbar": {
        width: "8px",
    },
    "&::-webkit-scrollbar-track": {
        background: SCROLLBAR_TRACK_COLOR,
        borderRadius: "999px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: SCROLLBAR_THUMB_COLOR,
        border: "2px solid transparent",
        backgroundClip: "padding-box",
        borderRadius: "999px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: SCROLLBAR_THUMB_HOVER_COLOR,
    },
};

const checklistContainerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.04,
        },
    },
};

const checklistItemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
    },
};

function getStatusLabel(reason, message) {
    if (reason === "cs2_hours_not_public") {
        return "Game details are private";
    }
    if (message) {
        return message;
    }
    return DEFAULT_BLOCKED_MESSAGE;
}

function ChecklistStep({ step, index }) {
    return (
        <MotionBox
            variants={checklistItemVariants}
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "14px",
                p: "14px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.015)",
                border: "1px solid rgba(255,255,255,0.05)",
                transition: "background 220ms ease, border-color 220ms ease",
                "&:hover": {
                    background: "rgba(232,163,61,0.04)",
                    borderColor: alpha(colors.primary.main, 0.18),
                },
                ...(index < CHECKLIST_STEPS.length - 1 && {
                    mb: "10px",
                }),
            }}
        >
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: colors.primary.main,
                    background: alpha(colors.primary.main, 0.1),
                    border: `1px solid ${alpha(colors.primary.main, 0.22)}`,
                }}
            >
                {step.number}
            </Box>

            <Box sx={{ minWidth: 0, pt: "2px" }}>
                {step.title ? (
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: colors.text.primary,
                            lineHeight: 1.55,
                        }}
                    >
                        {step.title}
                    </Typography>
                ) : null}
                {step.label ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                            mt: step.title ? 0 : "2px",
                        }}
                    >
                        {step.prefix ? (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: colors.text.primary,
                                    lineHeight: 1.55,
                                }}
                            >
                                {step.prefix}
                            </Typography>
                        ) : null}
                        <Box
                            component="span"
                            sx={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: colors.text.primary,
                                px: "10px",
                                py: "6px",
                                borderRadius: "8px",
                                background: alpha(colors.background.dark, 0.85),
                                border: `1px solid ${colors.border.default}`,
                            }}
                        >
                            {step.label}
                        </Box>
                        {step.suffix ? (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: colors.text.primary,
                                    lineHeight: 1.55,
                                }}
                            >
                                {step.suffix}
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}
            </Box>
        </MotionBox>
    );
}

ChecklistStep.propTypes = {
    step: PropTypes.shape({
        number: PropTypes.string.isRequired,
        title: PropTypes.string,
        prefix: PropTypes.string,
        label: PropTypes.string,
        suffix: PropTypes.string,
    }).isRequired,
    index: PropTypes.number.isRequired,
};

export default function SteamPrivacyCheckDialog({
    open,
    onClose,
    message,
    reason = null,
    blockedMembers = [],
    isParty = false,
}) {
    if (!open || typeof document === "undefined") {
        return null;
    }

    const showChecklist = reason === "cs2_hours_not_public";
    const showSteamButton = reason === "cs2_hours_not_public";
    const statusLabel = getStatusLabel(reason, message);
    const resolvedMembers = (blockedMembers || [])
        .map((member) => member?.nickname || member?.userId)
        .filter(Boolean);
    const showBlockedTeammates = isParty && resolvedMembers.length > 0;

    return ReactDOM.createPortal(
        <BackdropOverlay
            open={open}
            onClick={onClose}
            zIndex={DIALOG_Z_INDEX}
            sx={{
                inset: 0,
                width: "100%",
                height: "100dvh",
                minHeight: "100dvh",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                pt: DIALOG_OVERLAY_TOP_OFFSET,
                pb: { xs: 2, sm: 3 },
                boxSizing: "border-box",
                backdropFilter: "blur(10px)",
                backgroundColor: "rgba(7,7,8,0.78)",
            }}
        >
            <MotionBox
                role="dialog"
                aria-labelledby="steam-privacy-title"
                aria-modal="true"
                initial={{ opacity: 0, scale: 0.97, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                onClick={(event) => event.stopPropagation()}
                sx={{
                    position: "relative",
                    width: 560,
                    maxWidth: "92vw",
                    maxHeight: "calc(100dvh - 48px)",
                    borderRadius: DIALOG_PAPER_RADIUS,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    background: `
                        linear-gradient(
                            180deg,
                            ${alpha(colors.background.surface, 0.98)},
                            ${alpha(colors.background.card, 0.98)}
                        )
                    `,
                    border: `1px solid ${alpha(colors.primary.main, 0.18)}`,
                    boxShadow: `
                        0 40px 90px rgba(0,0,0,0.65),
                        inset 0 1px rgba(255,255,255,0.04)
                    `,
                }}
            >
                <Box
                    sx={{
                        height: "2px",
                        background: `
                            linear-gradient(
                                90deg,
                                transparent,
                                ${colors.primary.main},
                                transparent
                            )
                        `,
                    }}
                />

                <Box
                    sx={{
                        flexShrink: 0,
                        px: "32px",
                        pt: "24px",
                        pb: "20px",
                        background: `
                            linear-gradient(
                                180deg,
                                rgba(232,163,61,0.07),
                                transparent
                            )
                        `,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "16px",
                        }}
                    >
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "14px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                background: `
                                    linear-gradient(
                                        180deg,
                                        rgba(232,163,61,0.2),
                                        rgba(232,163,61,0.08)
                                    )
                                `,
                                border: `1px solid ${alpha(colors.primary.main, 0.28)}`,
                                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
                            }}
                        >
                            <WarningAmberOutlinedIcon
                                sx={{ color: colors.primary.main, fontSize: "1.35rem" }}
                            />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    letterSpacing: "0.22em",
                                    textTransform: "uppercase",
                                    color: colors.primary.main,
                                    mb: "8px",
                                }}
                            >
                                Steam verification
                            </Typography>
                            <Typography
                                id="steam-privacy-title"
                                component="h2"
                                sx={{
                                    fontSize: { xs: "28px", sm: "32px" },
                                    fontWeight: 700,
                                    letterSpacing: "-0.03em",
                                    color: colors.text.primary,
                                    lineHeight: 1.08,
                                    mb: "8px",
                                }}
                            >
                                Steam verification required
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    lineHeight: 1.6,
                                    color: colors.text.secondary,
                                }}
                            >
                                {message || DEFAULT_BLOCKED_MESSAGE}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box
                    data-lenis-prevent
                    data-lenis-prevent-wheel
                    data-lenis-prevent-touch
                    sx={{
                        ...SCROLLABLE_BODY_SX,
                        px: "32px",
                        pb: "20px",
                    }}
                >
                    <Box
                        sx={{
                            p: "16px",
                            borderRadius: "14px",
                            background: alpha(colors.background.surface, 0.92),
                            border: `1px solid ${colors.border.default}`,
                            borderLeft: `3px solid ${colors.primary.main}`,
                            mb: "20px",
                        }}
                    >
                        <Typography sx={SECTION_TITLE_SX}>Steam status</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Typography
                                sx={{
                                    fontSize: "16px",
                                    lineHeight: 1,
                                    color: colors.state.danger.main,
                                }}
                            >
                                ✕
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: colors.text.primary,
                                    lineHeight: 1.5,
                                }}
                            >
                                {statusLabel}
                            </Typography>
                        </Box>
                    </Box>

                    {showChecklist ? (
                        <Box sx={{ mb: "20px" }}>
                            <Typography sx={SECTION_TITLE_SX}>How to fix</Typography>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    lineHeight: 1.65,
                                    color: colors.text.secondary,
                                    mb: "14px",
                                }}
                            >
                                {CHECKLIST_INTRO}
                            </Typography>
                            <MotionBox
                                variants={checklistContainerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {CHECKLIST_STEPS.map((step, index) => (
                                    <ChecklistStep
                                        key={step.number}
                                        step={step}
                                        index={index}
                                    />
                                ))}
                            </MotionBox>
                        </Box>
                    ) : null}

                    {showChecklist ? (
                        <Box
                            sx={{
                                display: "flex",
                                gap: "12px",
                                alignItems: "flex-start",
                                p: "16px",
                                borderRadius: "14px",
                                mb: showBlockedTeammates ? "20px" : 0,
                                background: alpha(colors.primary.main, 0.06),
                                border: `1px solid ${alpha(colors.primary.main, 0.14)}`,
                            }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    color: colors.primary.main,
                                    fontSize: "1.1rem",
                                    mt: "2px",
                                    flexShrink: 0,
                                }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: colors.primary.main,
                                        mb: "6px",
                                    }}
                                >
                                    Info
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "14px",
                                        lineHeight: 1.65,
                                        color: colors.text.secondary,
                                    }}
                                >
                                    Steam changes may take up to a minute to update.{" "}
                                    {CHECKLIST_FOLLOW_UP}
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}

                    {!showChecklist ? (
                        <Typography
                            sx={{
                                fontSize: "14px",
                                lineHeight: 1.65,
                                color: colors.text.secondary,
                                mb: showBlockedTeammates ? "20px" : 0,
                            }}
                        >
                            {message || DEFAULT_BLOCKED_MESSAGE}
                        </Typography>
                    ) : null}

                    {showBlockedTeammates ? (
                        <Box>
                            <Typography sx={SECTION_TITLE_SX}>Blocked teammates</Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                }}
                            >
                                {resolvedMembers.map((name) => (
                                    <Box
                                        key={name}
                                        sx={{
                                            px: "12px",
                                            py: "8px",
                                            borderRadius: "999px",
                                            fontSize: "13px",
                                            fontWeight: 600,
                                            color: colors.text.primary,
                                            background: alpha(colors.background.surface, 0.95),
                                            border: `1px solid ${colors.border.default}`,
                                        }}
                                    >
                                        {name}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        flexShrink: 0,
                        height: "1px",
                        mx: "32px",
                        background: `
                            linear-gradient(
                                90deg,
                                transparent,
                                rgba(255,255,255,0.08),
                                transparent
                            )
                        `,
                    }}
                />

                <Box
                    sx={{
                        flexShrink: 0,
                        px: { xs: "20px", sm: "32px" },
                        py: { xs: "18px", sm: "24px" },
                        display: "flex",
                        flexDirection: { xs: "column-reverse", sm: "row" },
                        justifyContent: { xs: "stretch", sm: "flex-end" },
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: "12px",
                        flexWrap: { xs: "nowrap", sm: "wrap" },
                    }}
                >
                    <Button
                        onClick={onClose}
                        sx={{
                            height: "42px",
                            px: "18px",
                            width: { xs: "100%", sm: "auto" },
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 600,
                            color: colors.text.secondary,
                            background: "rgba(255,255,255,0.03)",
                            border: `1px solid ${colors.border.default}`,
                            boxShadow: "none",
                            transition:
                                "transform 180ms ease, background 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
                            "&:hover": {
                                background: "rgba(255,255,255,0.06)",
                                borderColor: alpha(colors.primary.main, 0.22),
                                boxShadow: "none",
                            },
                        }}
                    >
                        Skip for now
                    </Button>

                    {showSteamButton ? (
                        <Button
                            component="a"
                            href={STEAM_SETTINGS_URL}
                            target="_blank"
                            rel="noreferrer"
                            endIcon={<ArrowForwardIcon sx={{ fontSize: "1rem !important" }} />}
                            sx={{
                                height: "42px",
                                px: "20px",
                                width: { xs: "100%", sm: "auto" },
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: colors.text.contrast,
                                background: `
                                    linear-gradient(
                                        180deg,
                                        #F1B24B,
                                        #D89128
                                    )
                                `,
                                border: "1px solid rgba(255,255,255,0.08)",
                                boxShadow: `
                                    0 10px 20px rgba(232,163,61,0.15),
                                    inset 0 1px 0 rgba(255,255,255,0.15)
                                `,
                                transition:
                                    "transform 180ms ease, filter 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    filter: "brightness(1.04)",
                                    borderColor: "rgba(255,255,255,0.14)",
                                    boxShadow: `
                                        0 14px 26px rgba(232,163,61,0.2),
                                        inset 0 1px 0 rgba(255,255,255,0.18)
                                    `,
                                },
                            }}
                        >
                            Open Steam Settings
                        </Button>
                    ) : (
                        <Button
                            onClick={onClose}
                            sx={{
                                height: "42px",
                                px: "20px",
                                width: { xs: "100%", sm: "auto" },
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: colors.text.contrast,
                                background: `
                                    linear-gradient(
                                        180deg,
                                        #F1B24B,
                                        #D89128
                                    )
                                `,
                                border: "1px solid rgba(255,255,255,0.08)",
                                boxShadow: `
                                    0 10px 20px rgba(232,163,61,0.15),
                                    inset 0 1px 0 rgba(255,255,255,0.15)
                                `,
                                transition:
                                    "transform 180ms ease, filter 180ms ease, box-shadow 180ms ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    filter: "brightness(1.04)",
                                    boxShadow: `
                                        0 14px 26px rgba(232,163,61,0.2),
                                        inset 0 1px 0 rgba(255,255,255,0.18)
                                    `,
                                },
                            }}
                        >
                            I understand
                        </Button>
                    )}
                </Box>
            </MotionBox>
        </BackdropOverlay>,
        document.body,
    );
}

SteamPrivacyCheckDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    message: PropTypes.string,
    reason: PropTypes.string,
    blockedMembers: PropTypes.arrayOf(PropTypes.object),
    isParty: PropTypes.bool,
};
